import { useRef, useEffect } from 'react'

/**
 * Returns a new IntersectionObserver object using the provided callback and options.
 * For information on the IntersectionObserver API, see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API.
 *
 * @NB: This hook returns null for older browsers like IE that don't support the IntersectionObserver API
 */
export function useIntersectionObserver(
    inViewCallback: IntersectionObserverCallback,
    opts?: IntersectionObserverInit
) {
    const observerRef = useRef<IntersectionObserver | null>(
        typeof window === 'object' && window.IntersectionObserver
            ? new IntersectionObserver(inViewCallback, opts)
            : null
    )
    useEffect(() => {
        if (!window.IntersectionObserver) {
            // unsupported browser
            return
        }
        observerRef.current = new IntersectionObserver(inViewCallback, opts)
        return () => {
            observerRef.current!.disconnect()
        }
    }, [inViewCallback, opts])
    return observerRef
}
